import React, { useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { AnimatePresence } from 'framer-motion';
import { Popover } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  Chevron,
  ChipButton,
  ChipDivider,
  ChipLabel,
  ChipValues,
  popoverPaperSx,
} from '@/components/Approval/RightContainer/FilterBar/styles';
import {
  SummarySlot,
  useChipMotion,
} from '@/components/Approval/RightContainer/FilterBar/chipMotion';

type Props = {
  /** 칩 필터명 (제목 / 내용) */
  label: string;
  /** 현재 검색어 (없으면 미선택) */
  value: string;
  /** 검색 실행 */
  onSearch: (keyword: string) => void;
  /** 검색어 지우기 */
  onClear: () => void;
  /** 필터 더보기로 갓 추가된 칩이면 마운트되자마자 팝오버를 연다 */
  autoOpen?: boolean;
  /** autoOpen 처리 후 신호 초기화 */
  onAutoOpened?: () => void;
};

/**
 * 제목/내용 키워드 검색 칩 — 시안: 클릭 시 검색어 입력창 + '검색' 버튼 팝오버.
 * 제목/내용은 각각 독립 검색어(filters.titleKeyword / contentKeyword)를 가지며,
 * BE titleKeyword/contentKeyword 파라미터로 각각 전달되어 AND 조합된다.
 */
const KeywordChipPopover = ({
  label,
  value,
  onSearch,
  onClear,
  autoOpen,
  onAutoOpened,
}: Props) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [text, setText] = useState(value);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const open = Boolean(anchorEl);
  const { slotMotion } = useChipMotion();

  useEffect(() => {
    if (open) {
      setText(value);
      // 팝오버를 다시 열면 기존 검색어를 이어서 수정할 수 있도록 커서를 맨 뒤로 보낸다.
      setTimeout(() => {
        const el = inputRef.current;
        if (!el) return;
        el.focus();
        const end = el.value.length;
        el.setSelectionRange(end, end);
      }, 0);
    }
  }, [open, value]);

  useEffect(() => {
    if (autoOpen && buttonRef.current) {
      setAnchorEl(buttonRef.current);
      onAutoOpened?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoOpen]);

  const submit = () => {
    onSearch(text.trim());
    setAnchorEl(null);
  };

  return (
    <>
      <ChipButton
        ref={buttonRef}
        type="button"
        selected={!!value}
        open={open}
        onClick={(e) => setAnchorEl(e.currentTarget)}
      >
        <ChipLabel>{label}</ChipLabel>
        {/* 양식/결재자 칩과 같게, 값이 붙고 빠질 때 폭을 함께 전환한다 */}
        <AnimatePresence initial={false}>
          {value && (
            <SummarySlot key={'value'} {...slotMotion}>
              <ChipDivider />
              <ChipValues>
                <KeywordValue>
                  <ValueText>{value}</ValueText>
                  <ClearX
                    role="button"
                    aria-label="clear"
                    onClick={(e) => {
                      e.stopPropagation();
                      onClear();
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path
                        d="M12 4L4 12M4 4L12 12"
                        stroke="#888D96"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </ClearX>
                </KeywordValue>
              </ChipValues>
            </SummarySlot>
          )}
        </AnimatePresence>
        <Chevron open={open} aria-hidden>
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path
              d="M5.833 8L10 12.167L14.167 8"
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </Chevron>
      </ChipButton>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{ sx: { ...popoverPaperSx, width: '360px' } }}
      >
        <TextArea
          ref={inputRef}
          value={text}
          maxLength={500}
          placeholder={t('검색어를 입력해 주세요.')}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            // 한글 IME 조합 중 Enter 는 무시 (조합 중 마지막 글자 중복 방지)
            if (e.nativeEvent.isComposing) return;
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              submit();
            }
          }}
        />
        <SearchButton type="button" onClick={submit}>
          {t('검색')}
        </SearchButton>
      </Popover>
    </>
  );
};

export default KeywordChipPopover;

const KeywordValue = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 28px;
  box-sizing: border-box;
  padding: 4px 4px 4px 8px;
  background: var(--dk-bg, #ffffff);
  border: 1px solid var(--dk-line, #e7e9ec);
  border-radius: 4px;
  color: var(--dk-accent, #0066cc);
  font-size: 14px;
  line-height: 20px;
`;

/** 검색어가 길면 말줄임 */
const ValueText = styled.span`
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ClearX = styled.span`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  cursor: pointer;
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 120px;
  resize: none;
  border: none;
  outline: none;
  padding: 12px 16px;
  font-size: 14px;
  line-height: 20px;
  color: var(--dk-text, black);
  font-family: var(--font-Pretendard);
  &::placeholder {
    color: var(--dk-text-dim, #b7bbc2);
  }
`;

const SearchButton = styled.button`
  width: 100%;
  padding: 12px;
  text-align: center;
  font-size: 14px;
  color: var(--dk-text, black);
  background: var(--dk-bg, #ffffff);
  border-top: 1px solid var(--dk-line, #e7e9ec);
  cursor: pointer;
`;
